import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  CardGiftcard as CardGiftcardIcon,
  Flag as FlagIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  FamilyRestroom as FamilyIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  History as HistoryIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { userApi } from '../api/apiService';
import { User, LoginAttempt } from '../types/models';

const ParentManagementPage = () => {
  const { parent, kids, refreshKids } = useAuth();
  const [kidsList, setKidsList] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addKidDialogOpen, setAddKidDialogOpen] = useState(false);
  const [newKidName, setNewKidName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [kidToDelete, setKidToDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { userId } = useParams();

  // PIN management state
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [selectedKid, setSelectedKid] = useState<User | null>(null);
  const [generatedPin, setGeneratedPin] = useState<string | null>(null);
  const [generatedUsername, setGeneratedUsername] = useState<string | null>(null);
  const [pinCopied, setPinCopied] = useState(false);
  const [generatingPin, setGeneratingPin] = useState(false);

  // Login attempts state
  const [loginAttemptsDialogOpen, setLoginAttemptsDialogOpen] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  useEffect(() => {
    if (kids) {
      setKidsList(kids);
    }
  }, [kids]);

  const handleAddKid = async () => {
    if (!newKidName.trim() || !parent?.id) return;

    try {
      setSubmitting(true);
      setError(null);

      const newKid = {
        name: newKidName.trim(),
        totalPoints: 0,
        coins: 0
      };

      const response = await userApi.create(newKid);
      setKidsList([...kidsList, response.data]);
      setSuccess(`Successfully added ${newKidName}!`);
      setNewKidName('');
      setAddKidDialogOpen(false);

      // Refresh kids in AuthContext
      await refreshKids();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add kid');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (kid: User) => {
    setKidToDelete(kid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!kidToDelete?.id) return;

    try {
      setDeleting(true);
      setError(null);
      await userApi.delete(kidToDelete.id);
      setKidsList(kidsList.filter(k => k.id !== kidToDelete.id));
      setSuccess(`Successfully deleted ${kidToDelete.name}`);
      setDeleteDialogOpen(false);
      setKidToDelete(null);
      await refreshKids();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete kid');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setKidToDelete(null);
  };

  const handleKidClick = (kidId: string) => {
    navigate(`/user/${kidId}/targets`);
  };

  // PIN management functions
  const handleGeneratePin = async (kid: User) => {
    if (!kid.id) return;

    try {
      setGeneratingPin(true);
      setError(null);
      const response = await userApi.generatePin(kid.id);
      setGeneratedPin(response.data.pinCode);
      setGeneratedUsername(response.data.username);
      setSelectedKid(kid);
      setPinDialogOpen(true);
      await refreshKids();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate PIN');
    } finally {
      setGeneratingPin(false);
    }
  };

  const handleCopyPin = () => {
    if (generatedPin && generatedUsername) {
      const loginInfo = `Username: ${generatedUsername}\nPIN: ${generatedPin}`;
      navigator.clipboard.writeText(loginInfo);
      setPinCopied(true);
      setTimeout(() => setPinCopied(false), 2000);
    }
  };

  const handleViewLoginAttempts = async (kid: User) => {
    if (!kid.id) return;

    try {
      setLoadingAttempts(true);
      setSelectedKid(kid);
      setLoginAttemptsDialogOpen(true);
      const response = await userApi.getLoginAttempts(kid.id);
      setLoginAttempts(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load login attempts');
    } finally {
      setLoadingAttempts(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const features = [
    {
      icon: <FlagIcon color="primary" sx={{ fontSize: 40 }} />,
      title: 'Manage Targets',
      description: 'Create, edit, and assign targets for kids to complete.',
      onClick: () => userId && navigate(`/user/${userId}/targets`),
    },
    {
      icon: <CardGiftcardIcon color="secondary" sx={{ fontSize: 40 }} />,
      title: 'Manage Gifts',
      description: 'Add new gifts and set coin costs for redemption.',
      onClick: () => userId && navigate(`/user/${userId}/gifts`),
    },
    {
      icon: <FamilyIcon sx={{ fontSize: 40, color: '#6366f1' }} />,
      title: 'Family',
      description: 'Invite another parent to share managing your kids.',
      onClick: () => navigate('/family'),
    },
    {
      icon: <SettingsIcon color="secondary" sx={{ fontSize: 40 }} />,
      title: 'Settings',
      description: 'Configure point values and notification preferences.',
      onClick: undefined,
    },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Kids Management Section */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: 1,
              display: 'flex'
            }}>
              <PeopleIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                My Kids
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {kidsList.length} kid{kidsList.length !== 1 ? 's' : ''} registered
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddKidDialogOpen(true)}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Add Kid
          </Button>
        </Box>
        <CardContent>
          {kidsList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No kids added yet. Click "Add Kid" to get started!
              </Typography>
            </Box>
          ) : (
            <List>
              {kidsList.map((kid, index) => (
                <ListItem
                  key={kid.id}
                  divider={index < kidsList.length - 1}
                  sx={{
                    px: 0,
                    py: 2,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: 'grey.50' },
                    borderRadius: 1
                  }}
                  onClick={() => kid.id && handleKidClick(kid.id)}
                >
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2, fontWeight: 600 }}>
                    {kid.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {kid.name}
                        {kid.pinCode ? (
                          <Tooltip title="PIN is set - kid can login">
                            <Chip
                              icon={<LockIcon />}
                              label="Can Login"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="No PIN set - kid cannot login yet">
                            <Chip
                              icon={<LockOpenIcon />}
                              label="Setup PIN"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {kid.totalPoints} points • {kid.coins} coins
                        </Typography>
                        {kid.username && (
                          <Typography variant="caption" color="text.secondary">
                            Login: {kid.username}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Generate/Set PIN">
                      <IconButton
                        onClick={(e) => { e.stopPropagation(); handleGeneratePin(kid); }}
                        disabled={generatingPin}
                        sx={{ color: 'primary.main' }}
                      >
                        {kid.pinCode ? <LockIcon /> : <LockOpenIcon />}
                      </IconButton>
                    </Tooltip>
                    {kid.pinCode && (
                      <Tooltip title="View Login History">
                        <IconButton
                          onClick={(e) => { e.stopPropagation(); handleViewLoginAttempts(kid); }}
                          sx={{ color: 'info.main' }}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(kid); }}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Features Grid */}
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Quick Actions
      </Typography>
      <Grid container spacing={3}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s',
                cursor: feature.onClick ? 'pointer' : 'default',
                '&:hover': feature.onClick ? {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                } : {},
              }}
              onClick={feature.onClick}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'grey.100',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add Kid Dialog */}
      <Dialog open={addKidDialogOpen} onClose={() => setAddKidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Kid</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Kid's Name"
            value={newKidName}
            onChange={(e) => setNewKidName(e.target.value)}
            margin="normal"
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddKidDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddKid}
            disabled={submitting || !newKidName.trim()}
          >
            {submitting ? <CircularProgress size={24} /> : 'Add Kid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Kid</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{kidToDelete?.name}"? This action cannot be undone and all their points and coins will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PIN Generated Dialog */}
      <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedKid?.pinCode ? 'PIN Updated!' : 'PIN Generated!'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Share these credentials with {selectedKid?.name} so they can login on their own device.
          </Alert>
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Username:
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', mb: 2 }}>
              {generatedUsername}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              PIN Code:
            </Typography>
            <Typography variant="h4" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 700, letterSpacing: 4 }}>
              {generatedPin}
            </Typography>
          </Card>
          <Button
            fullWidth
            variant="outlined"
            startIcon={pinCopied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopyPin}
            color={pinCopied ? 'success' : 'primary'}
            sx={{ mt: 2 }}
          >
            {pinCopied ? 'Copied!' : 'Copy Login Info'}
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPinDialogOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Attempts Dialog */}
      <Dialog open={loginAttemptsDialogOpen} onClose={() => setLoginAttemptsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            Login History for {selectedKid?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingAttempts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : loginAttempts.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No login attempts recorded yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loginAttempts.map((attempt) => (
                  <TableRow key={attempt.id}>
                    <TableCell>
                      {attempt.success ? (
                        <Chip label="Success" color="success" size="small" icon={<CheckIcon />} />
                      ) : (
                        <Chip label="Failed" color="error" size="small" icon={<WarningIcon />} />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(attempt.createdAt.toString())}</TableCell>
                    <TableCell>{attempt.ipAddress || 'Unknown'}</TableCell>
                    <TableCell>
                      {attempt.failureReason || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setLoginAttemptsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentManagementPage;